'use client';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/app/components/ui/tooltip';
import { VoteType } from '@prisma/client';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { MessageSquare, ThumbsDown, ThumbsUp } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTourismItemEvents } from '../../contexts/TourismGlobalEvents';
// import { useTourismItemEvents } from '../contexts/TourismGlobalEvents'; // <-- проверь относительный путь

type InteractiveCardProps = {
  item: {
    id: string;
    initialLikes: number;
    initialDislikes: number;
    commentCount: number;
    myVote?: VoteType | null;
  };
  onViewDetails: (scrollToComments?: boolean) => void;
  onVote: (itemId: string, voteType: VoteType) => void;
};

export function InteractiveCard({ item, onViewDetails, onVote }: InteractiveCardProps) {
  // ✅ SSE только для этого item
  const ev = useTourismItemEvents(item.id);

  // ✅ Локальные значения (живут в карточке, не трогают общий список)
  const [likes, setLikes] = useState(item.initialLikes ?? 0);
  const [dislikes, setDislikes] = useState(item.initialDislikes ?? 0);
  const [comments, setComments] = useState(item.commentCount ?? 0);

  // ✅ если пришли новые пропсы (optimistic vote, pagination, revalidate) — синкаемся
  useEffect(() => setLikes(item.initialLikes ?? 0), [item.initialLikes]);
  useEffect(() => setDislikes(item.initialDislikes ?? 0), [item.initialDislikes]);
  useEffect(() => setComments(item.commentCount ?? 0), [item.commentCount]);

  // ✅ применяем SSE (авторитетнее пропсов)
  useEffect(() => {
    if (!ev) return;

    if (ev.type === 'VOTE_UPDATE') {
      // store уже дал событие только для этого id — но оставим защиту
      if (ev.payload.placeId && ev.payload.placeId !== item.id) return;
      if (ev.payload.suggestionId && ev.payload.suggestionId !== item.id) return;

      setLikes(ev.payload.likes);
      setDislikes(ev.payload.dislikes);
      return;
    }

    if (ev.type === 'NEW_COMMENT') {
      setComments((c) => c + 1);
      return;
    }

    if (ev.type === 'DELETE_COMMENT') {
      const n = ev.payload.commentIds?.length ?? 1;
      setComments((c) => Math.max(0, c - n));
    }
  }, [ev, item.id]);

  const handleVote = useCallback(
    (voteType: VoteType) => onVote(item.id, voteType),
    [item.id, onVote],
  );

  const { likeTooltip, dislikeTooltip } = useMemo(() => {
    let like = 'Нравится';
    let dislike = 'Не нравится';
    if (item.myVote === VoteType.LIKE) like = 'Отменить лайк';
    if (item.myVote === VoteType.DISLIKE) dislike = 'Отменить дизлайк';
    return { likeTooltip: like, dislikeTooltip: dislike };
  }, [item.myVote]);

  return (
    <section
      role="group"
      aria-label="Интерактивные действия с карточкой"
      className="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div
        className="flex items-center w-full justify-around gap-1 bg-slate-800/70 p-1 rounded-full sm:w-auto sm:justify-start sm:order-2"
        role="toolbar"
        aria-label="Оценки и комментарии"
      >
        {/* 👍 LIKE */}
        <Tooltip>
          <TooltipTrigger asChild>
            <motion.button
              onClick={() => handleVote(VoteType.LIKE)}
              whileTap={{ scale: 0.9 }}
              aria-label="Поставить лайк"
              aria-pressed={item.myVote === VoteType.LIKE}
              className="flex items-center gap-2 px-4 py-1.5 rounded-full hover:bg-slate-700 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-400"
            >
              <ThumbsUp
                className={clsx(
                  'transition-colors',
                  item.myVote === VoteType.LIKE ? 'text-sky-400' : 'text-slate-400',
                )}
                size={20}
                fill={item.myVote === VoteType.LIKE ? 'currentColor' : 'none'}
              />
              <span className="text-sm font-semibold text-white">{likes}</span>
            </motion.button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{likeTooltip}</p>
          </TooltipContent>
        </Tooltip>

        <div className="w-[1px] h-6 bg-slate-700" />

        {/* 👎 DISLIKE */}
        <Tooltip>
          <TooltipTrigger asChild>
            <motion.button
              onClick={() => handleVote(VoteType.DISLIKE)}
              whileTap={{ scale: 0.9 }}
              aria-label="Поставить дизлайк"
              aria-pressed={item.myVote === VoteType.DISLIKE}
              className="flex items-center gap-2 px-4 py-1.5 rounded-full hover:bg-slate-700 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-400"
            >
              <ThumbsDown
                className={clsx(
                  'transition-colors',
                  item.myVote === VoteType.DISLIKE ? 'text-red-400' : 'text-slate-400',
                )}
                size={20}
                fill={item.myVote === VoteType.DISLIKE ? 'currentColor' : 'none'}
              />
              <span className="text-sm font-semibold text-white">{dislikes}</span>
            </motion.button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{dislikeTooltip}</p>
          </TooltipContent>
        </Tooltip>

        <div className="w-[1px] h-5 bg-slate-700 mx-1" />

        {/* 💬 COMMENTS */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => onViewDetails(true)}
              aria-label={`Посмотреть комментарии: ${comments}`}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full hover:bg-slate-700 transition-colors"
            >
              <MessageSquare className="text-slate-400" size={16} />
              <span className="text-sm font-semibold text-white">{comments}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Посмотреть комментарии</p>
          </TooltipContent>
        </Tooltip>
      </div>

      <button
        onClick={() => onViewDetails(false)}
        className="px-4 py-2 text-sm font-semibold text-white bg-sky-600 rounded-full hover:bg-sky-700 transition-colors focus:outline-none focus:ring-2 focus:ring-sky-400 whitespace-nowrap sm:order-1 w-full sm:w-auto"
        type="button"
      >
        <span className="cursor-pointer">Подробнее</span>
      </button>
    </section>
  );
}
